import { notify } from "@/components/common/Notification";
import {
  getAccessToken,
  getRefreshToken,
  getTenantId,
  setLocalAccessToken,
} from "@/lib/storeManager";
import {
  forceLogoutAfterRefreshFailure,
  refreshAccessToken,
} from "@/services/tokenRefresh";
import { hidenMessageAPI } from "@/utils/constant/hidenMessageAPI";
import axios from "axios";

export const axiosConfig = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
  xsrfCookieName: "csrftoken",
  xsrfHeaderName: "X-CSRFToken",
});

axiosConfig.interceptors.request.use(config => {
  const token = getAccessToken();
  const tenantId = getTenantId();

  // Don't add Authorization header for refresh token requests
  if (
    token &&
    config.headers &&
    !config.url?.includes("/auth/token-refresh/")
  ) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (tenantId && config.headers) {
    config.headers["X-Tenant-Id"] = tenantId;
  } else {
    alert("No tenantId found: apiClient");
  }

  return config;
});

let isRefreshing = false;
let failedQueue: {
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

function isOnAdminPanel() {
  return (
    typeof window !== "undefined" &&
    window.location.pathname.startsWith("/admin")
  );
}

function notifyApiError(error: any) {
  // Admin pages show their own errors (inline + sonner). Skip auto antd toasts.
  if (isOnAdminPanel()) {
    return;
  }
  notify.error(
    error?.response?.data?.message ??
      error?.response?.data?.error?.message ??
      error?.response?.data?.error
  );
}

axiosConfig.interceptors.response.use(
  response => {
    const apiStatus = response.status;
    const method = (response.config.method ?? "get").toLowerCase();

    // Never toast on GET — admin/teacher list & dashboard fetches return
    // success messages like "Data fetched successfully".
    if (method === "get") {
      return response;
    }

    if (
      hidenMessageAPI.some(path => (response.config.url ?? "").includes(path))
    ) {
      return response;
    }

    // Skip automatic antd success toasts on admin panel — pages already show
    // their own sonner toasts (e.g. "Topic updated"). Avoids "Updated successfully".
    if (isOnAdminPanel()) {
      return response;
    }

    if (
      (apiStatus === 200 || apiStatus === 201 || apiStatus === 204) &&
      response.data?.success
    ) {
      const message = response.data?.success?.message ?? response.data.message;
      if (message) {
        notify.success(message);
      }
    }
    return response;
  },
  async error => {
    const originalRequest = error.config;
    const method = (originalRequest?.method ?? "get").toLowerCase();

    // Suppress automatic error toasts for GET; pages show their own message.
    if (method === "get" && error.response?.status !== 401) {
      return Promise.reject(error);
    }

    if (
      error.response?.status === 400 ||
      error.response?.status === 404 ||
      error.response?.status === 417
    ) {
      notifyApiError(error);
      return Promise.reject(error);
    }
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      getRefreshToken()
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          return axiosConfig(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newAccessToken = await refreshAccessToken();
        if (!newAccessToken) {
          throw new Error("Unable to refresh access token.");
        }
        setLocalAccessToken(newAccessToken);
        axiosConfig.defaults.headers.common["Authorization"] =
          `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        return axiosConfig(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        forceLogoutAfterRefreshFailure();
        window.location.reload();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    } else {
      notifyApiError(error);
      return Promise.reject(error);
    }
  }
);
